<template>
  <div class="col-12 sm:col-10 md:col-9 lg:col-6 pr-2 lg:pr-6">
    <div class="review row md:mx-0 lg:mx-n2 pb-3 md:pb-4 lg:pb-5">
      <div class="col-2 lg:col-3 pr-0 md:pl-0 lg:pl-2 lg:pr-3">
        <div
          v-if="comment.imageUrl"
          class="embed-responsive embed-responsive-1by1 mb-0 lg:mb-2 overflow-visible"
        >
          <RemoteImage
            :src="comment.imageUrl"
            img-class="embed-responsive-item image-cover pattern-2 bg-white"
            :alt="comment.shortName"
            sizes="70vw"
          />
        </div>
        <div
          v-else
          class="embed-responsive embed-responsive-1by1 mb-0 lg:mb-2 overflow-visible"
        >
          <div class="embed-responsive-item image-cover pattern-2 bg-white" />
          <div class="absolute  bottom-0 left-0 w-120 h-120">
            <BirdSkin class="w-100 mb-n1" />
          </div>
        </div>
      </div>
      <div class="col-10 lg:col-9 pl-3 md:pl-4 lg:pl-0 lg:px-2 md:mr-0 flex flex-col">
        <div class="shadow block relative py-3 px-4 rounded-lg comment-bubble">
          <div class="absolute right-0 top-0 mt-n4 mr-n5">
            <TopCommentSvg />
          </div>
          <h4>{{ t('top_comment') }}</h4>
          <div
            class="flex justify-start flex-wrap"
            :class="registeredUser ? 'mb-1' : 'mb-3'"
          >
            <div
              v-if="comment.verifiedComment"
              v-tooltip="{
                content: t('verified_user_message'),
                theme: 'verifiedUserTooltip',
              }"
              class="absolute zindex-11 mt-n5 ml-n5"
            >
              <VerifiedUserSvg />
            </div>
            <strong class="px-0 col-auto order-0">{{ comment.shortName }}</strong>
            <ProductStarsRating
              v-if="comment.starCount"
              :rating="comment.starCount"
              origin="cs"
              :show-rating="false"
              :show-origin="false"
              :mobile-minified="false"
              add-class="lg:order-1 order-2 lg:ml-2 ml-0 lg:mt-0 mt-1 lg:flex-full"
            />
            <span class="col-auto flex-shrink-0 ml-auto lg:order-2 order-1 pr-0 pl-0">{{ comment.createdAt }}</span>
          </div>
          <div
            v-if="registeredUser && !comment.verifiedComment"
            class="flex mb-2 items-center"
          >
            <span>{{ t('registered_user') }}</span>
            <span
              v-tooltip="{
                content: t('registered_user_message'),
                theme: 'baseTooltip',
              }"
              class="bg-warning p-1 rounded-circle ml-1"
            >
              <i class="mi-info icon-14" />
            </span>
          </div>
          <span
            v-if="comment.isMerged"
            class="auto-translate mb-1 block text-muted mt-2"
          >
            <AutoTranslate />
            {{ t('translated_review') }}
          </span>
          <p class="mb-1 lg:mb-4">
            {{ comment.body }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

import RemoteImage from '@/components/general/BaseRemoteImage.vue'
import ProductStarsRating from '@/components/products/ProductStarsRating.vue'
import TopCommentSvg from '@/components/svg/TopCommentSvg.vue'
import VerifiedUserSvg from '@/components/svg/VerifiedUserSvg.vue'
import BirdSkin from '@/components/svg/birds/BirdSkin.vue'
import AutoTranslate from '../svg/AutoTranslate.vue'

const { t } = useI18n()

const registeredUser = true

defineProps({
  comment: {
    type: Object,
    default() {
      return {}
    },
  },
})
</script>

<style lang="scss">
.lg\:flex-full {
  flex: 0 0 100%;
  @media (min-width: 992px) {
    flex: auto;
  }
}
.comment-bubble {
  &:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid #f7e7d1;
    right: 100%;
    top: 50px;

    @media screen and (max-width: 991px) {
      top: 30px;
      border-right: 10px solid $white;
    }
    @media screen and (max-width: 767px) {
      top: 20px;
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "registered_user": "Registrovaný zákazník",
    "registered_user_message": "Komentář napsal zaregistrovaný zákazník",
    "top_comment": "Top komentář",
    "translated_review": "Automatický překlad",
    "verified_user_message": "Ověřený nákup - zákazník produkt zakoupil na e-shopu Mixit"
},
    "sk": {
    "registered_user": "Registrovaný zákazník",
    "registered_user_message": "Komentár napísal registrovaný zákazník",
    "top_comment": "Top komentár",
    "translated_review": "Automatický preklad",
    "verified_user_message": "Overený nákup - zákazník produkt zakúpil na e-shope Mixit"
},
    "pl": {
    "registered_user": "Zarejestrowany klient",
    "registered_user_message": "Komentarz zarejestrowanego klienta.",
    "top_comment": "Najlepszy komentarz",
    "translated_review": "Tłumaczenie automatyczne",
    "verified_user_message": "Zweryfikowany zakup - klient zakupił produkt w sklepie internetowym Mixit"
},
    "hu": {
    "registered_user": "Regisztrált ügyfél",
    "registered_user_message": "Ezt értékelést Mixit regisztrált ügyfél írta.",
    "top_comment": "Top megjegyzés",
    "translated_review": "Automatikus fordítás",
    "verified_user_message": "Ellenőrzött vásárlás - vásárló a Mixit webshopban vásárolta meg ezt a terméket"
},
    "de": {
    "registered_user": "Registrierter Kunde",
    "registered_user_message": "Dieser Kommentar wurde von einem Kunden geschrieben, der bei Mixit registriert ist",
    "top_comment": "Top-Kommentar",
    "translated_review": "Automatische Übersetzung",
    "verified_user_message": "Verifizierter Kauf - Der Kunde hat dar Produkt bereits gekauft. "
},
    "at": {
    "registered_user": "Registrierter Kunde",
    "registered_user_message": "Dieser Kommentar wurde von einem Kunden geschrieben, der bei Mixit registriert ist",
    "top_comment": "Top-Kommentar",
    "translated_review": "Automatische Übersetzung",
    "verified_user_message": "Verifizierter Kauf - Der Kunde hat dar Produkt bereits gekauft. "
},
    "ro": {
    "registered_user": "Client înregistrat",
    "registered_user_message": "Comentariu scris de un client înregistrat",
    "top_comment": "Comentariu de top",
    "translated_review": "Traducere automată",
    "verified_user_message": "Achiziție verificată - clientul a cumpărat produsul de pe e-shopul Mixit."
},
    "fr": {
    "registered_user": "Client enregistré",
    "registered_user_message": "Commentaire écrit par un client enregistré",
    "top_comment": "Commentaire le plus populaire",
    "translated_review": "Traduction automatique",
    "verified_user_message": "Achat vérifié : le client a acheté le produit sur l’e-shop de Mixit"
},
    "it": {
    "registered_user": "Cliente registrato",
    "registered_user_message": "Commento scritto da un cliente registrato",
    "top_comment": "Commento top",
    "translated_review": "Traduzione automatica",
    "verified_user_message": "Acquisto verificato - il cliente ha acquistato il prodotto sull'e-shop Mixit"
}
  }
</i18n>
